import { DOCUMENT } from '@angular/common';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input, signal } from '@angular/core';
import { injectDialogRef } from '@workbuddy/ui/v2/blocks/dialog';
import { ButtonComponent, ButtonDisplayStyle, ButtonLinkComponent, ButtonSize } from '@workbuddy/ui/v2/components/button';
import { CalendarRange, MiniCalendarComponent, MiniCalendarMode } from '@workbuddy/ui/v2/components/calendar';
import { DesignSystem } from '@workbuddy/ui/v2/design-system';
import { OutlineBlackoutRange, formatBlackoutSelection, isBlackoutRangeValid, outlineBlackoutRange } from './outline.set.blackout.model';
import { outlineSetBlackoutDialogBridgeStyles, outlineSetBlackoutDialogStyleId } from './outline.set.blackout.dialog.theme';

/**
 * Picking a pause is a range job, so the dialog is a two-month range picker: both grids under one
 * prev/next pair, the span painted across them, and a single summary line saying what has been picked
 * and what is still owed. The From/To read-outs it replaced looked like fields and were not.
 */
@Component({
  selector: 'service-plan-outline-set-blackout-dialog',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  imports: [ButtonComponent, ButtonLinkComponent, MiniCalendarComponent],
  templateUrl: './outline.set.blackout.dialog.html',
  styleUrls: ['./outline.set.blackout.dialog.less'],
})
export class OutlineSetBlackoutDialogComponent {

  private readonly document = inject(DOCUMENT);
  private readonly dialogRef = injectDialogRef<OutlineBlackoutRange>();

  protected readonly primary = ButtonDisplayStyle.Primary;
  protected readonly buttonSize = ButtonSize.Medium;
  protected readonly linkSize = ButtonSize.Small;
  protected readonly MiniCalendarMode = MiniCalendarMode;
  protected readonly titleId = `service-plan-set-blackout-title-${crypto.randomUUID()}`;

  /** The one sentence that says how far this pause reaches — plan wording or line wording. */
  public readonly $caption = input('', { alias: 'caption' });

  /**
   * Opens empty rather than pre-picked. A seeded range would leave `Add blackout` live on a span the
   * user never chose, and the summary line exists precisely to ask for the two clicks.
   */
  protected readonly $range = signal<CalendarRange>({});

  protected readonly $reason = signal<string>('');

  protected readonly $summary = computed(() => formatBlackoutSelection(this.$range()));

  /** A half-open range is still being picked; only a closed, non-inverted one can be written. */
  protected readonly $canAdd = computed(() => isBlackoutRangeValid(this.$range()));

  /**
   * `Clear` appears the moment a start exists, not only once the range is closed. A half-open pick is
   * the state a user is most likely to want out of — the calendar's own next click would close the
   * range rather than abandon it, so without this there is no way back to an empty selection.
   */
  protected readonly $canClear = computed(() => Boolean(this.$range().from));

  public constructor() {

    DesignSystem.Mount(this.document, outlineSetBlackoutDialogStyleId, outlineSetBlackoutDialogBridgeStyles);
  }

  protected onRangeChange(range: CalendarRange): void {

    this.$range.set(range);
  }

  /** Back to square one: the summary returns to its prompt and the primary goes dead with it. */
  protected clearRange(): void {

    this.$range.set({});
  }

  protected onReasonInput(event: Event): void {

    this.$reason.set((event.target as HTMLInputElement).value);
  }

  protected cancel(): void {

    this.dialogRef.dismiss();
  }

  protected add(): void {

    const range = this.$range();
    if (!isBlackoutRangeValid(range)) return;
    this.dialogRef.close(outlineBlackoutRange(range.from as string, range.to as string, this.$reason()));
  }
}
